import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";

const cleanText = (value) => (value === undefined || value === null ? "" : String(value).trim());
const toPositiveInt = (value, fallback = 1) => {
  const parsed = Math.trunc(Number(value));
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};
const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const normalizeStorefrontPricingRules = (value = {}) => {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  return {
    version: 1,
    enabled: source.enabled === true,
    minimumQuantity: toPositiveInt(source.minimumQuantity ?? source.minimumQty, 1),
    quantityStep: toPositiveInt(source.quantityStep, 1),
    aggregateAcrossVariations: source.aggregateAcrossVariations !== false,
    separateInventoryFromPricing: source.separateInventoryFromPricing === true,
    tiers: (Array.isArray(source.tiers) ? source.tiers : [])
      .map((tier) => ({
        minQty: toPositiveInt(tier?.minQty ?? tier?.quantity, 1),
        unitPrice: toNumber(tier?.unitPrice ?? tier?.price, 0),
        netCost:
          tier?.netCost === null || tier?.netCost === undefined || tier?.netCost === ""
            ? null
            : toNumber(tier.netCost, 0),
      }))
      .sort((a, b) => a.minQty - b.minQty),
    sizeAdjustments: (Array.isArray(source.sizeAdjustments) ? source.sizeAdjustments : [])
      .map((adjustment) => ({
        size: cleanText(adjustment?.size ?? adjustment?.option),
        amount: toNumber(adjustment?.amount ?? adjustment?.addPerUnit ?? adjustment?.add_per_unit, 0),
      })),
    variationImageOverrides: (
      Array.isArray(source.variationImageOverrides) ? source.variationImageOverrides : []
    )
      .map((override) => ({
        color: cleanText(override?.color),
        size: cleanText(override?.size),
        imageUrl: cleanText(override?.imageUrl ?? override?.url),
      })),
  };
};

const money = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? `$${number.toFixed(2)}` : "$0.00";
};

const PromoStoreStorefrontPricingRules = ({
  value,
  onChange,
  colorOptions = [],
  sizeOptions = [],
  imageUrls = [],
}) => {
  const rules = useMemo(() => normalizeStorefrontPricingRules(value), [value]);
  const [expanded, setExpanded] = useState(false);
  const normalizedColorOptions = useMemo(
    () => (colorOptions || [])
      .map((option) => typeof option === "string" ? option : option?.name ?? option?.label ?? option?.id ?? "")
      .map(cleanText)
      .filter(Boolean),
    [colorOptions]
  );
  const normalizedSizeOptions = useMemo(
    () => (sizeOptions || [])
      .map((option) => typeof option === "string" ? option : option?.name ?? option?.label ?? option?.id ?? "")
      .map(cleanText)
      .filter(Boolean),
    [sizeOptions]
  );

  const patch = (next) => onChange?.(normalizeStorefrontPricingRules({ ...rules, ...next }));

  const updateTier = (index, key, nextValue) => {
    const tiers = rules.tiers.map((tier, tierIndex) =>
      tierIndex === index ? { ...tier, [key]: nextValue } : tier
    );
    patch({ tiers });
  };

  const addTier = () => {
    const last = rules.tiers[rules.tiers.length - 1];
    patch({
      tiers: [
        ...rules.tiers,
        {
          minQty: last ? Math.max(last.minQty + 1, rules.minimumQuantity) : rules.minimumQuantity,
          unitPrice: last?.unitPrice ?? 0,
          netCost: last?.netCost ?? null,
        },
      ],
    });
  };

  const removeTier = (index) => {
    patch({ tiers: rules.tiers.filter((_tier, tierIndex) => tierIndex !== index) });
  };

  const updateSizeAdjustment = (index, key, nextValue) => {
    const rows = rules.sizeAdjustments.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [key]: nextValue } : row
    );
    patch({ sizeAdjustments: rows });
  };

  const addSizeAdjustment = () => {
    patch({
      sizeAdjustments: [...rules.sizeAdjustments, { size: "", amount: 0 }],
    });
  };

  const removeSizeAdjustment = (index) => {
    patch({
      sizeAdjustments: rules.sizeAdjustments.filter((_row, rowIndex) => rowIndex !== index),
    });
  };

  const updateImageOverride = (index, key, nextValue) => {
    const rows = rules.variationImageOverrides.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [key]: nextValue } : row
    );
    patch({ variationImageOverrides: rows });
  };

  const addImageOverride = () => {
    patch({
      variationImageOverrides: [
        ...rules.variationImageOverrides,
        { color: "", size: "", imageUrl: imageUrls[0] || "" },
      ],
    });
  };

  const removeImageOverride = (index) => {
    patch({
      variationImageOverrides: rules.variationImageOverrides.filter(
        (_row, rowIndex) => rowIndex !== index
      ),
    });
  };

  const summaryChips = [
    rules.enabled ? `MOQ ${rules.minimumQuantity}` : "Off",
    rules.enabled ? `${rules.tiers.length} price break${rules.tiers.length === 1 ? "" : "s"}` : null,
    rules.enabled && rules.sizeAdjustments.length
      ? `${rules.sizeAdjustments.length} size adjustment${rules.sizeAdjustments.length === 1 ? "" : "s"}`
      : null,
    rules.enabled && rules.aggregateAcrossVariations ? "Mixed variants combine" : null,
  ].filter(Boolean);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_event, nextExpanded) => setExpanded(nextExpanded)}
      disableGutters
      sx={{
        mt: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px !important",
        boxShadow: "none",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 62,
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            minWidth: 0,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <PriceChangeOutlinedIcon color="primary" />
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={700}>Storefront pricing & quantity rules</Typography>
            <Typography variant="caption" color="text.secondary">
              PromoStore only · controls customer-facing quantity pricing without changing project pricing.
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            gap: 0.5,
            mr: 1,
          }}
        >
          {summaryChips.map((label) => (
            <Chip
              key={label}
              label={label}
              size="small"
              variant={rules.enabled ? "outlined" : "filled"}
            />
          ))}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <Box sx={{ display: "grid", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: "grey.50",
            }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Advanced storefront pricing
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Turn this on only when this product needs quantity breaks, a minimum order, or storefront-only size pricing.
              </Typography>
            </Box>
            <Switch
              checked={rules.enabled}
              onChange={(event) =>
                patch({
                  enabled: event.target.checked,
                  ...(event.target.checked ? { separateInventoryFromPricing: true } : {}),
                })
              }
            />
          </Box>

          {rules.enabled && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"
                  type="number"
                  label="Minimum order quantity"
                  value={rules.minimumQuantity}
                  inputProps={{ min: 1, step: 1 }}
                  onChange={(event) =>
                    patch({ minimumQuantity: toPositiveInt(event.target.value, 1) })
                  }
                  helperText="Enforced at cart / checkout."
                />
                <TextField
                  size="small"
                  type="number"
                  label="Quantity step"
                  value={rules.quantityStep}
                  inputProps={{ min: 1, step: 1 }}
                  onChange={(event) =>
                    patch({ quantityStep: toPositiveInt(event.target.value, 1) })
                  }
                  helperText="Usually 1 unless sold in packs."
                />
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.5,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={rules.aggregateAcrossVariations}
                        onChange={(event) =>
                          patch({ aggregateAcrossVariations: event.target.checked })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Combine colour / size quantities
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          10 Black + 14 Navy counts as 24 for price breaks.
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Box>

              <Alert severity="info">
                Storefront price-break quantities are kept separate from inventory. A quantity threshold such as 24 will never be treated as “24 units in stock.”
              </Alert>

              <Accordion
                defaultExpanded
                disableGutters
                sx={{ boxShadow: "none", border: "1px solid", borderColor: "divider" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography fontWeight={700}>Quantity pricing</Typography>
                    <Chip size="small" label={`${rules.tiers.length} tier${rules.tiers.length === 1 ? "" : "s"}`} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: "grid", gap: 1 }}>
                    {rules.tiers.map((tier, index) => {
                      const margin =
                        tier.netCost != null && tier.unitPrice > 0
                          ? ((tier.unitPrice - tier.netCost) / tier.unitPrice) * 100
                          : null;
                      return (
                        <Box
                          key={`tier-${index}`}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "120px minmax(140px, 1fr) minmax(140px, 1fr) 100px 36px",
                            gap: 1,
                            alignItems: "center",
                            "@media (max-width: 800px)": {
                              gridTemplateColumns: "1fr 1fr",
                            },
                          }}
                        >
                          <TextField
                            size="small"
                            type="number"
                            label="From qty"
                            value={tier.minQty}
                            inputProps={{ min: 1, step: 1 }}
                            onChange={(event) =>
                              updateTier(index, "minQty", toPositiveInt(event.target.value, 1))
                            }
                          />
                          <TextField
                            size="small"
                            type="number"
                            label="Customer price / unit"
                            value={tier.unitPrice}
                            inputProps={{ min: 0, step: "0.01" }}
                            onChange={(event) =>
                              updateTier(index, "unitPrice", toNumber(event.target.value, 0))
                            }
                          />
                          <TextField
                            size="small"
                            type="number"
                            label="Net cost / unit"
                            value={tier.netCost ?? ""}
                            inputProps={{ min: 0, step: "0.01" }}
                            onChange={(event) =>
                              updateTier(
                                index,
                                "netCost",
                                event.target.value === "" ? null : toNumber(event.target.value, 0)
                              )
                            }
                            helperText="Internal only"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {margin == null ? "Margin —" : `${margin.toFixed(1)}% margin`}
                          </Typography>
                          <Tooltip title="Remove tier">
                            <span>
                              <IconButton
                                size="small"
                                disabled={rules.tiers.length <= 1}
                                onClick={() => removeTier(index)}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      );
                    })}
                    {!rules.tiers.length && (
                      <Typography variant="body2" color="text.secondary">
                        Add at least one customer-facing price tier.
                      </Typography>
                    )}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addTier}
                      sx={{ justifySelf: "start", textTransform: "none" }}
                    >
                      Add quantity tier
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters
                sx={{ boxShadow: "none", border: "1px solid", borderColor: "divider" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography fontWeight={700}>Size price adjustments</Typography>
                    <Chip
                      size="small"
                      label={`${rules.sizeAdjustments.length} override${rules.sizeAdjustments.length === 1 ? "" : "s"}`}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Add only exceptions such as 2XL +$4.00. Normal sizes inherit the active quantity-tier price.
                  </Typography>
                  <Box sx={{ display: "grid", gap: 1 }}>
                    {rules.sizeAdjustments.map((row, index) => (
                      <Box
                        key={`size-adjustment-${index}`}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "minmax(180px, 1fr) 180px 36px",
                          gap: 1,
                          alignItems: "center",
                        }}
                      >
                        <Autocomplete
                          freeSolo
                          size="small"
                          options={normalizedSizeOptions}
                          value={row.size}
                          onInputChange={(_event, nextValue) =>
                            updateSizeAdjustment(index, "size", nextValue)
                          }
                          onChange={(_event, nextValue) =>
                            updateSizeAdjustment(index, "size", cleanText(nextValue))
                          }
                          renderInput={(params) => <TextField {...params} label="Size" />}
                        />
                        <TextField
                          size="small"
                          type="number"
                          label="Add per unit"
                          value={row.amount}
                          inputProps={{ step: "0.01" }}
                          onChange={(event) =>
                            updateSizeAdjustment(index, "amount", toNumber(event.target.value, 0))
                          }
                        />
                        <IconButton size="small" onClick={() => removeSizeAdjustment(index)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addSizeAdjustment}
                      sx={{ justifySelf: "start", textTransform: "none" }}
                    >
                      Add size adjustment
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters
                sx={{ boxShadow: "none", border: "1px solid", borderColor: "divider" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <ImageOutlinedIcon fontSize="small" />
                    <Typography fontWeight={700}>Variation image overrides</Typography>
                    <Chip
                      size="small"
                      label={`${rules.variationImageOverrides.length} mapped`}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Optional. Map only the colour / size combinations that should switch the storefront image. Unmapped variants keep the normal product gallery.
                  </Typography>
                  <Box sx={{ display: "grid", gap: 1 }}>
                    {rules.variationImageOverrides.map((row, index) => (
                      <Box
                        key={`image-override-${index}`}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "minmax(140px, 1fr) minmax(120px, .8fr) minmax(220px, 1.6fr) 36px",
                          gap: 1,
                          alignItems: "center",
                          "@media (max-width: 900px)": {
                            gridTemplateColumns: "1fr 1fr",
                          },
                        }}
                      >
                        <Autocomplete
                          freeSolo
                          size="small"
                          options={normalizedColorOptions}
                          value={row.color}
                          onInputChange={(_event, nextValue) =>
                            updateImageOverride(index, "color", nextValue)
                          }
                          onChange={(_event, nextValue) =>
                            updateImageOverride(index, "color", cleanText(nextValue))
                          }
                          renderInput={(params) => <TextField {...params} label="Colour" />}
                        />
                        <Autocomplete
                          freeSolo
                          size="small"
                          options={["", ...normalizedSizeOptions]}
                          value={row.size}
                          onInputChange={(_event, nextValue) =>
                            updateImageOverride(index, "size", nextValue)
                          }
                          onChange={(_event, nextValue) =>
                            updateImageOverride(index, "size", cleanText(nextValue))
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Size (optional)" />
                          )}
                        />
                        <Select
                          size="small"
                          displayEmpty
                          value={row.imageUrl}
                          onChange={(event) =>
                            updateImageOverride(index, "imageUrl", event.target.value)
                          }
                        >
                          <MenuItem value="">
                            <em>Select product image</em>
                          </MenuItem>
                          {imageUrls.map((url, imageIndex) => (
                            <MenuItem key={`${url}-${imageIndex}`} value={url}>
                              {cleanText(url).split("/").pop() || `Image ${imageIndex + 1}`}
                            </MenuItem>
                          ))}
                        </Select>
                        <IconButton size="small" onClick={() => removeImageOverride(index)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addImageOverride}
                      disabled={!imageUrls.length}
                      sx={{ justifySelf: "start", textTransform: "none" }}
                    >
                      Add image override
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Divider />
              <Typography variant="caption" color="text.secondary">
                Storefront rules are PromoStore-specific. They are not applied to Projects, Estimates, Sales Orders, or Showcase products.
              </Typography>
            </>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default PromoStoreStorefrontPricingRules;
